import { AsyncPipe, CommonModule, isPlatformBrowser } from '@angular/common';
import type {
	OnChanges,
	OnInit,
	SimpleChange,
	SimpleChanges,
} from '@angular/core';
import {
	Component,
	computed,
	effect,
	inject,
	Input,
	input,
	model,
	output,
	PLATFORM_ID,
	signal,
	untracked,
} from '@angular/core';
import { RouterLink } from '@angular/router';

import { TranslatePipe } from '@ngx-translate/core';
import dayjs from 'dayjs';
import type { Observable } from 'rxjs';
import {
	BehaviorSubject,
	distinctUntilChanged,
	filter,
	map,
	of,
	shareReplay,
	switchMap,
	tap,
} from 'rxjs';

import type {
	AvailabilityDatesInterface,
	AvailabilityRequestInterface,
} from '@valk-nx/availability-dates-store/availability-dates.interface';
import { AvailabilityDatesService } from '@valk-nx/availability-dates-store/availability-dates.service';
import { TabMenuComponent } from '@valk-nx/components/ui-tab-menu/src/lib/tab-menu';
import { TabMenuItemDirective } from '@valk-nx/components/ui-tab-menu/src/lib/tab-menu.directive';
import {
	cn,
	fallbackLanguage,
	LANGUAGE,
	toQueryString,
} from '@valk-nx/core/lib/core';
import type {
	AccommodationTypeInterface,
	HotelInterface,
	HotelOccupancyRestrictionsInterface,
	OccupancyRestrictionsInterface,
} from '@valk-nx/core/lib/interfaces/hotels.interface';
import { BookingRedirectHelper } from '@valk-nx/helpers/lib/booking-redirect/booking-redirect.helper';
import { getMaxBookingDate } from '@valk-nx/helpers/lib/dates/advance-booking.helper';
import type { OccupancyInterface } from '@valk-nx/helpers/lib/interfaces/occupancy.interface';
import { OccupancyHelper } from '@valk-nx/helpers/lib/occupancy/occupancy.helper';
import { toDate } from '@valk-nx/helpers/lib/transformers/toDate';

import { AvailabilityWidgetInputDateRangeComponent } from './availability-input-date-range/availability-input-date-range';
import { AvailabilityWidgetInputDropdownComponent } from './availability-input-dropdown/availability-input-dropdown';
import type { AvailabilityInputDropdownItem } from './availability-input-dropdown/availability-input-dropdown.interface';
import { AvailabilityWidgetInputOccupancyComponent } from './availability-input-occupancy/availability-input-occupancy';
import { validateDates } from './availability-date-validation.helper';
import type { AvailabilityResolution } from './availability-resolution';
import { shouldKeepSelection } from './availability-resolution';
import type { WidgetDataActionInterface } from './availability-widget.component.interface';
import { generateVhiAvailability } from './vhi-availability.helper';
import { generateVhiBookingUrl } from './vhi-redirect.helper';

export const occupancyRestrictions = {
	adults: {
		min: 1,
		max: 3,
	},
	children: {
		min: 0,
		max: 1,
	},
	infants: {
		min: 0,
		max: 1,
	},
	maxPersons: 2,
	maxOccupancies: 5,
};

const yesterday = dayjs().subtract(1, 'day').toDate();
type AvailabilityWidgetInput = 'hotels' | 'occupancy' | 'packages' | 'dates';

@Component({
	selector: `vp-availability-widget`,
	templateUrl: './availability-widget.component.html',
	imports: [
		AvailabilityWidgetInputDateRangeComponent,
		AvailabilityWidgetInputOccupancyComponent,
		AvailabilityWidgetInputDropdownComponent,
		AsyncPipe,
		CommonModule,
		TabMenuComponent,
		TabMenuItemDirective,
		TranslatePipe,
		RouterLink,
	],
})
export class AvailabilityWidgetComponent implements OnInit, OnChanges {
	private readonly availabilityService = inject(AvailabilityDatesService);
	private readonly mergedAvailability = signal<AvailabilityDatesInterface>(
		{},
	);
	readonly platformId = inject(PLATFORM_ID);
	readonly defaultLanguage = inject(LANGUAGE);
	private readonly updateCta = signal(0);

	@Input() hotelGuid: string | undefined;
	hotelSlug = input<string | undefined>(undefined);
	@Input() roomGuid: string | undefined;
	@Input() dealGuid: string | undefined;
	packageGuid = input<string | undefined>(undefined);
	promotionGuid = input<string | undefined>(undefined);
	groupGuid = input<string | undefined>(undefined);
	suppressAvailability = input(false);
	contactUrl = input<string>('');
	isGroupBooking = computed(() => !!this.groupGuid());

	accommodationTypes = input<AccommodationTypeInterface[] | undefined>();
	activeAccommodationTypeIndex = signal(0);
	selectedAccommodationType = model<string | undefined>(undefined);
	@Input() initialOccupancy: OccupancyInterface[] | undefined;
	@Input() defaultAdults = 2;
	occupancyRestrictions = input<OccupancyRestrictionsInterface>(
		occupancyRestrictions,
	);

	@Input({ transform: toDate }) initialArrivalDate: Date | undefined;
	@Input({ transform: toDate }) initialDepartureDate: Date | undefined;
	minDate = input<Date | undefined>(undefined);
	maxDate = input<Date | undefined>(undefined);

	hotels = input<AvailabilityInputDropdownItem[]>([]);
	@Input() stepperConfig: {
		useStepper: boolean;
		min: number;
		max: number | undefined;
	} = { useStepper: false, min: 1, max: undefined };

	currency = input<string>('EUR');
	showOccupancy = input(true);
	showDates = input(true);
	showPrices = input(true);
	showPackages = input(false);
	showAccommodationTypes = input(false);
	packages = input<AvailabilityInputDropdownItem[]>([]);
	forceHideRangePrices = input<boolean>(false);
	hotelsWithOccupancyRestrictions = input<
		HotelOccupancyRestrictionsInterface[] | null
	>(null);
	showHotels = input(false);

	openAvailabilityInNewTab = input<boolean>(false);

	actionClick = output<{
		event: string;
		eventType: string;
		data: WidgetDataActionInterface;
	}>();
	packageSelected = output<number>();
	occupancyChanged = output<OccupancyInterface[]>();
	hotelChanged = output<Partial<HotelInterface>>();
	availabilityResolved = output<AvailabilityDatesInterface>();
	// The dates the widget chose itself after a code is entered or removed.
	// The configuration page saves them and reloads the rooms for them.
	datesAutoSelected = output<{ arrivalDate: Date; departureDate: Date }>();

	bookingtoolUrl = input<string>();
	useLocalizedBookingRoute = input<boolean>(false);
	language = input<string>(fallbackLanguage);

	occupancy: OccupancyInterface[];

	updateAvailability$ = new BehaviorSubject<AvailabilityRequestInterface>(
		undefined,
	);
	isLoading = false;
	jumpToFirstAvailableDate = true;

	availability$: Observable<AvailabilityDatesInterface>;
	currentAvailability = signal<AvailabilityDatesInterface | null>(null);
	firstDateInCalendar: string = dayjs().format('YYYY-MM-DD');
	openInput = signal<AvailabilityWidgetInput | null>(null);
	isOccupancyOpen = computed(() => this.openInput() === 'occupancy');
	isHotelsOpen = computed(() => this.openInput() === 'hotels');
	isPackagesOpen = computed(() => this.openInput() === 'packages');
	isDatePickerOpen = computed(() => this.openInput() === 'dates');

	/**
	 * Determines the applicable occupancy restrictions based on the selected accommodation type.
	 * If a specific accommodation type is selected, and it defines its own restrictions,
	 * those are used. Otherwise, it falls back to the default occupancy restrictions.
	 * The fallback for undefined occupancyRestrictions is for if the component uses the old data structure.
	 * Group bookings narrow the result to what a group reservation supports.
	 */
	activeOccupancyRestrictions = computed(() => {
		const restrictions = this.baseOccupancyRestrictions();
		return this.isGroupBooking()
			? OccupancyHelper.applyGroupBookingRestrictions(restrictions)
			: restrictions;
	});

	private readonly baseOccupancyRestrictions = computed(() => {
		const selectedGuid = this.selectedAccommodationType();
		const types = this.accommodationTypes();
		if (selectedGuid && types && !this.showHotels()) {
			const selectedType = types.find(
				(type) => type.guid === selectedGuid,
			);
			if (selectedType.occupancyRestrictions) {
				return selectedType.occupancyRestrictions;
			}
		} else if (this.hotelsWithOccupancyRestrictions()) {
			const hotelsRestrictions = this.hotelsWithOccupancyRestrictions();
			const selectedHotelRestrictions = hotelsRestrictions?.find(
				(hotel) => {
					return hotel.guid === this.selectedHotel()?.guid;
				},
			);

			if (selectedHotelRestrictions) {
				return selectedHotelRestrictions.occupancyRestrictions;
			}
		}
		return this.occupancyRestrictions();
	});

	/**
	 * The hotels to offer for selection, in the order that is received by the component —
	 * that order is meaningful, so do not sort here. Falls back to the hotels
	 * carried by the occupancy restrictions when the hotels input is empty.
	 */
	hotelsList = computed(() => {
		if (!this.showHotels() && this.hotelSlug()) {
			return null;
		}

		return this.hotels().length > 0
			? this.hotels()
			: this.hotelsWithOccupancyRestrictions();
	});

	isVhiHotelSelected = computed(() => {
		const selectedGuid = this.selectedHotel()?.guid;
		if (!selectedGuid) return false;
		return (
			this.hotelsWithOccupancyRestrictions()?.find(
				(hotel) => hotel.guid === selectedGuid,
			)?.branch === 'VHI'
		);
	});

	// IDPMS code of the selected VHI hotel, used to build its booking redirect URL.
	private readonly selectedVhiCode = computed(() => {
		const selectedGuid = this.selectedHotel()?.guid;
		if (!selectedGuid) return undefined;
		return this.hotelsWithOccupancyRestrictions()?.find(
			(hotel) => hotel.guid === selectedGuid,
		)?.code;
	});

	private readonly selectedHotelMaxBookingMonths = computed(() => {
		const guid = this.selectedHotel()?.guid;
		if (!guid) return undefined;

		const fromRestrictions = this.hotelsWithOccupancyRestrictions()?.find(
			(hotel) => hotel.guid === guid,
		)?.maxAdvanceBookingMonths;
		const fromDropdown = this.hotels().find(
			(hotel) => hotel.guid === guid,
		)?.maxAdvanceBookingMonths;

		return fromRestrictions ?? fromDropdown;
	});

	readonly effectiveMaxDate = computed(() => {
		if (this.isVhiHotelSelected()) {
			return getMaxBookingDate(undefined, true);
		}

		const maxDate = this.maxDate();
		if (maxDate) {
			return maxDate;
		}

		return getMaxBookingDate(this.selectedHotelMaxBookingMonths());
	});

	readonly effectiveMinDate = computed(() => this.minDate() ?? yesterday, {
		equal: (a, b) => a.getTime() === b.getTime(),
	});

	// A minDate is only a real constraint when it is after today; a
	// today-or-earlier value is the default and counts as no minDate.
	private readonly hasExplicitMinDate = computed(() => {
		const minDate = this.minDate();
		return (
			minDate !== undefined &&
			dayjs(minDate).isAfter(dayjs().subtract(1, 'day'), 'day')
		);
	});

	selectedHotel = signal<Partial<HotelInterface> | undefined>(undefined);
	selectedPackage = model<AvailabilityInputDropdownItem>();
	nights = 1;

	arrivalDate: Date | null = null;
	departureDate: Date | null = null;
	_resetDateRange = 0;
	private readonly resolvedPackage = computed(
		() => {
			const guid = this.packageGuid();
			return this.packages()?.find(
				// TODO: Fix typing in VPL-31625
				(item) => (item.guid ?? item['GUID']) === guid,
			);
		},
		{
			equal: (a, b) =>
				(a?.guid ?? a?.['GUID']) === (b?.guid ?? b?.['GUID']),
		},
	);
	_dateUpdateSource: 'stepper' | 'programmatic' | 'manual' | null = null;
	// Set by the change that triggers a request, captured when the request is
	// sent and restored when its response arrives (see AvailabilityResolution)
	private availabilityResolution: AvailabilityResolution = 'validate';
	lastRequestKey: string | null = null;
	lastRequestDateRange: {
		startDate: string;
		endDate: string;
	} | null = null;

	activeHotelCurrency = computed(() => {
		if (this.selectedHotel()) {
			return this.selectedHotel()?.currency;
		}
		return this.currency();
	});
	hasMultiplePackages = computed(() => this.packages()?.length > 1);
	nextInputAfterHotel = computed<AvailabilityWidgetInput>(() => {
		if (this.showOccupancy()) {
			return 'occupancy';
		}

		return this.hasMultiplePackages() ? 'packages' : 'dates';
	});
	nextInputAfterOccupancy = computed<AvailabilityWidgetInput>(() => {
		return this.hasMultiplePackages() ? 'packages' : 'dates';
	});

	cn = cn;

	constructor() {
		effect(() => {
			this.effectiveMinDate();
			this.applyMinDate();
		});

		effect(() => {
			const availability = this.currentAvailability();
			if (!availability) {
				return;
			}
			this.resolveAvailability(availability);
		});

		this.availability$ = this.updateAvailability$.pipe(
			filter(
				(request) =>
					!!request && !!request.hotelGUID && !!request.occupancies,
			),
			distinctUntilChanged(areAvailabilityRequestsEqual),
			tap(() => (this.isLoading = true)),
			switchMap((request) => {
				// Create a key to detect when key parameters change (hotel, occupancy, etc.)
				const requestKey = this.getRequestKey(request);
				const shouldReset = this.lastRequestKey !== requestKey;
				const resolution = this.availabilityResolution;

				if (shouldReset) {
					this.lastRequestKey = requestKey;
					this.mergedAvailability.set({});
				}

				// Track the date range for this request
				this.lastRequestDateRange = {
					startDate: request.startDate,
					endDate: request.endDate,
				};

				// The response restores the resolution this request was sent
				// with: a later change (e.g. month navigation) may already have
				// set a new one. switchMap drops superseded requests, so only
				// the latest request's resolution is ever applied.
				return this.fetchAvailability(request, shouldReset).pipe(
					tap(() => (this.availabilityResolution = resolution)),
				);
			}),
			tap((availability) => {
				this.isLoading = false;
				this.currentAvailability.set(availability);
				this.availabilityResolved.emit(availability);
			}),
			shareReplay(1),
		);

		effect(() => {
			const resolvedPackage = this.resolvedPackage();
			if (!resolvedPackage) {
				return;
			}
			this.selectedPackage.set(resolvedPackage);
			this.getAvailability();
		});

		// if a selectedAccommodatinTypeGuid changes, the input has been changed
		effect(() => {
			const selectedAccommodationTypeGuid =
				this.selectedAccommodationType();
			if (!selectedAccommodationTypeGuid) {
				this.activeAccommodationTypeIndex.set(0);
				return;
			}

			const selectedindex = this.accommodationTypes().findIndex(
				(accommodationType) =>
					accommodationType.guid === selectedAccommodationTypeGuid,
			);

			// we have to compensate 'All' which is at index 0
			this.activeAccommodationTypeIndex.set(selectedindex + 1);
		});

		// if the activeAccommodationType changes, an accommodationType tab as been changed
		effect(() => {
			const index = this.activeAccommodationTypeIndex();
			if (index === 0 || index > this.accommodationTypes().length) {
				// 'All'
				this.selectedAccommodationType.set(undefined);
			} else {
				this.selectedAccommodationType.set(
					this.accommodationTypes()[index - 1]?.guid,
				);
			}
			this.getAvailability();
		});
	}

	ngOnChanges(changes: SimpleChanges) {
		// Track when dates are set programmatically (from @Input, e.g., URL params)
		if (changes['initialArrivalDate'] || changes['initialDepartureDate']) {
			this._dateUpdateSource = 'programmatic';
		}

		if (changes['initialOccupancy'] && this.showOccupancy()) {
			const newInitialOccupancy =
				changes['initialOccupancy'].currentValue;
			// Only update if the value actually changed (prevent loops from parent recalculations)
			if (
				newInitialOccupancy &&
				JSON.stringify(newInitialOccupancy) !==
					JSON.stringify(this.occupancy)
			) {
				this.occupancy = newInitialOccupancy;
			}
		}

		if (changes['occupancyRestrictions']) {
			this.getAvailability();
		}

		if (this.scopeChanged(changes)) {
			this.handleScopeChange(changes);
		}

		this.updateCta.update((v) => v + 1);
	}

	ngOnInit() {
		this.nights = this.stepperConfig?.min ?? 1;

		if (!this.showOccupancy()) {
			this.occupancy = [
				{ adults: this.defaultAdults, children: 0, infants: 0 },
			];
		} else {
			this.occupancy = this.initialOccupancy;
		}

		// Sync tab index from initial parent value (only once, not in an effect)
		const initialGuid = this.selectedAccommodationType();
		if (initialGuid && this.accommodationTypes()) {
			const selectedindex = this.accommodationTypes().findIndex(
				(accommodationType) => accommodationType.guid === initialGuid,
			);
			if (selectedindex >= 0) {
				this.activeAccommodationTypeIndex.set(selectedindex + 1);
			}
		}

		// Capture the seed's arrival month before the stepper validation below can
		// clear an invalid seed, so the re-pick stays in the entered month.
		if (
			this.initialArrivalDate &&
			!this.hasExplicitMinDate() &&
			!dayjs(this.initialArrivalDate).isBefore(
				this.effectiveMinDate(),
				'day',
			)
		) {
			this.firstDateInCalendar = dayjs(this.initialArrivalDate)
				.startOf('month')
				.format('YYYY-MM-DD');
		}

		// Validate dates against stepperConfig constraints (resolver ensures deal is loaded)
		this.validateDatesAgainstStepperConfig();
		this.setNights();
	}

	private scopeChanged(changes: SimpleChanges): boolean {
		return (
			this.changedAfterInit(changes['groupGuid']) ||
			this.changedAfterInit(changes['promotionGuid']) ||
			this.changedAfterInit(changes['suppressAvailability'])
		);
	}

	// A group/promo code or suppressAvailability change alters which dates are
	// available. Removing a code with both dates selected keeps them; every
	// other scope change discards them and re-picks the first available date.
	private handleScopeChange(changes: SimpleChanges): void {
		const arrivalDate = this.initialArrivalDate;
		const departureDate = this.initialDepartureDate;
		const keepSelection = shouldKeepSelection({
			suppressChanged: this.changedAfterInit(
				changes['suppressAvailability'],
			),
			groupRemoved: this.codeWasRemoved(changes['groupGuid']),
			promotionRemoved: this.codeWasRemoved(changes['promotionGuid']),
			hasSelectedDates: !!arrivalDate && !!departureDate,
		});
		if (keepSelection) {
			this.keepDatesOnCodeRemoval(arrivalDate, departureDate);
			return;
		}
		this.resetForScopeChange();
	}

	private changedAfterInit(change: SimpleChange | undefined): boolean {
		return !!change && !change.firstChange;
	}

	private codeWasRemoved(change: SimpleChange | undefined): boolean {
		return !!change?.previousValue && !change.currentValue;
	}

	// The kept dates are not validated against the regular availability that
	// comes back: when that range has no rooms, the host shows its regular
	// no-results warning instead of the dates silently disappearing. The emit
	// makes the host refetch rooms without the code.
	private keepDatesOnCodeRemoval(
		arrivalDate: Date,
		departureDate: Date,
	): void {
		this.availabilityResolution = 'keep-selection';
		this.jumpToFirstAvailableDate = false;
		this.getAvailability();
		this.datesAutoSelected.emit({ arrivalDate, departureDate });
	}

	private clearSelectedDates() {
		this.initialArrivalDate = undefined;
		this.initialDepartureDate = undefined;
		this.arrivalDate = null;
		this.departureDate = null;
	}

	// Discards the selection and re-picks the first available date for the new
	// scope. Clearing availability makes the pick wait for the refetch.
	private resetForScopeChange() {
		this.clearSelectedDates();
		this.availabilityResolution = 'pick-first-and-emit';
		this.currentAvailability.set(null);
		this.firstDateInCalendar = this.hasExplicitMinDate()
			? dayjs(this.effectiveMinDate()).format('YYYY-MM-DD')
			: dayjs().format('YYYY-MM-DD');
		this.jumpToFirstAvailableDate = true;
		this.getAvailability();
	}

	// Positions the calendar at the earliest selectable date and
	// re-selects the first available date on/after it.
	private applyMinDate() {
		const earliestDate = this.effectiveMinDate();

		if (this.hasExplicitMinDate()) {
			this.firstDateInCalendar = dayjs(earliestDate).format('YYYY-MM-DD');

			// An explicit minDate invalidates any earlier seed/selection.
			if (
				this.initialArrivalDate &&
				dayjs(this.initialArrivalDate).isBefore(earliestDate, 'day')
			) {
				this.initialArrivalDate = undefined;
				this.initialDepartureDate = undefined;
			}
			if (
				this.arrivalDate &&
				dayjs(this.arrivalDate).isBefore(earliestDate, 'day')
			) {
				this.arrivalDate = null;
				this.departureDate = null;
			}
		} else if (this.initialArrivalDate) {
			this.firstDateInCalendar = dayjs(this.initialArrivalDate)
				.startOf('month')
				.format('YYYY-MM-DD');
		}

		this.syncDateResolution();
		// The availability effect does not re-run for a minDate change
		// (setInitialDates reads it untracked), so try the loaded availability
		// here. Without a date on/after the new minDate the pick stays pending
		// for the refetch below.
		const availability = untracked(() => this.currentAvailability());
		if (availability && this.isPickPending()) {
			this.pickFirstAvailableDate(availability);
		}
		this.getAvailability();
	}

	private isPickPending(): boolean {
		return (
			this.availabilityResolution === 'pick-first' ||
			this.availabilityResolution === 'pick-first-and-emit'
		);
	}

	private cancelPendingDatePick(): void {
		if (this.isPickPending()) {
			this.availabilityResolution = 'validate';
		}
	}

	// With both dates selected the calendar opens on them (no jump) and a
	// pending pick is dropped. Without them the next response picks the first
	// available date, keeping the emit a scope change may already have asked for.
	private syncDateResolution(): void {
		const datesMissing =
			!this.initialArrivalDate || !this.initialDepartureDate;
		this.jumpToFirstAvailableDate = datesMissing;
		if (!datesMissing) {
			this.cancelPendingDatePick();
		} else if (this.availabilityResolution !== 'pick-first-and-emit') {
			this.availabilityResolution = 'pick-first';
		}
	}

	// Runs (via the availability effect) for every response and applies the
	// resolution its request was sent with.
	private resolveAvailability(
		availability: AvailabilityDatesInterface,
	): void {
		switch (this.availabilityResolution) {
			case 'keep-selection':
				// This response belongs to the removal. Keep the dates for it;
				// the following response may validate them again.
				this.availabilityResolution = 'validate';
				break;
			case 'pick-first':
			case 'pick-first-and-emit':
				this.pickFirstAvailableDate(availability);
				break;
			case 'validate':
				this.validateSelectionAgainstAvailability(availability);
				break;
		}
		this._dateUpdateSource = null;
	}

	private pickFirstAvailableDate(
		availability: AvailabilityDatesInterface,
	): void {
		const emit = this.availabilityResolution === 'pick-first-and-emit';
		this.setInitialDates(availability);
		// Nothing available in this range: stay pending, so a later response
		// can still pick (month navigation cancels it, see onMonthChange).
		if (!this.initialArrivalDate) {
			return;
		}
		this.availabilityResolution = 'validate';
		if (emit && this.initialDepartureDate) {
			this.datesAutoSelected.emit({
				arrivalDate: this.initialArrivalDate,
				departureDate: this.initialDepartureDate,
			});
		}
	}

	private fetchAvailability(
		request: AvailabilityRequestInterface,
		shouldReset: boolean,
	): Observable<AvailabilityDatesInterface> {
		// Suppressed availability must not fall back to an unscoped
		// request: answer empty so every date reads as unavailable.
		if (request.suppressAvailability) {
			return of(
				this.mergeAvailability(
					{},
					request.startDate,
					request.endDate,
					true,
				),
			);
		}

		if (this.isVhiHotelSelected()) {
			const vhiData = generateVhiAvailability(
				request.startDate,
				request.endDate,
			);
			return of(
				this.mergeAvailability(
					vhiData,
					request.startDate,
					request.endDate,
					shouldReset,
				),
			);
		}

		return this.availabilityService
			.getAvailabilityDates(
				request.hotelGUID,
				request.endDate,
				request.startDate,
				request.occupancies,
				request.accommodationTypes,
				request.dealGUID,
				request.packageGUID,
				request.roomGUID,
				request.nights,
				request.promotionGUID,
				request.groupGUID,
			)
			.pipe(
				map((newAvailability) =>
					this.mergeAvailability(
						newAvailability,
						request.startDate,
						request.endDate,
						shouldReset,
					),
				),
			);
	}

	private validateSelectionAgainstAvailability(
		availability: AvailabilityDatesInterface,
	): void {
		if (this.initialArrivalDate && this.initialDepartureDate) {
			this.validateDatesAgainstAvailability();
			if (this._dateUpdateSource === 'programmatic') {
				this._dateUpdateSource = null;
			}
		}
		if (this.arrivalDate) {
			this.validateSelectedArrivalDate(availability);
		}
	}

	// sets the intialArrivalDate and initialDepartureDate if they are not set yet, based on the given availability dates response
	private setInitialDates(availability: AvailabilityDatesInterface) {
		const arrivalDates = Object.keys(availability);
		if (!this.initialArrivalDate && arrivalDates.length > 0) {
			// Read outside the availability effect's dependencies. A later
			// minDate change is handled by applyMinDate; tracking it here
			// would replay this effect on the availability it already resolved.
			const earliestDate = untracked(() =>
				this.hasExplicitMinDate()
					? dayjs(this.effectiveMinDate()).format('YYYY-MM-DD')
					: undefined,
			);
			const firstAvailable = earliestDate
				? arrivalDates.find((date) => date >= earliestDate)
				: arrivalDates[0];
			if (firstAvailable) {
				this.initialArrivalDate = toDate(firstAvailable) as Date;
			}
		}
		if (!this.initialDepartureDate && this.initialArrivalDate) {
			const arrivalKey = dayjs(this.initialArrivalDate).format(
				'YYYY-MM-DD',
			);
			const departureDates = availability[arrivalKey];
			if (departureDates && Object.keys(departureDates).length > 0) {
				this.initialDepartureDate = toDate(
					Object.keys(departureDates)[0],
				) as Date;
			}
		}
	}

	private validateDatesAgainstStepperConfig() {
		if (!this.hasDates() || !this.stepperConfig?.useStepper) {
			return;
		}

		const calculatedNights = this.calculateNights();
		const { min = 1, max } = this.stepperConfig;

		const isValid =
			calculatedNights >= min && (!max || calculatedNights <= max);

		if (!isValid) {
			this.clearDatesAndResetNights(min);
		}
	}

	private setNights() {
		if (this.stepperConfig?.useStepper && this.hasDates()) {
			this.nights = this.calculateNights();
		}
	}

	private validateDatesAgainstAvailability() {
		if (!this.hasDates()) {
			return;
		}

		// Skip validation for stepper and manual updates
		// But validate programmatic updates (from URL/Input) to clear invalid dates
		if (
			this._dateUpdateSource === 'stepper' ||
			this._dateUpdateSource === 'manual'
		) {
			return;
		}

		const availability = this.currentAvailability();
		if (!availability) {
			return;
		}

		const arrivalKey = dayjs(this.initialArrivalDate).format('YYYY-MM-DD');
		const departureKey = dayjs(this.initialDepartureDate).format(
			'YYYY-MM-DD',
		);

		const isAvailable = !!availability[arrivalKey]?.[departureKey];

		if (!isAvailable) {
			this.clearDatesAndResetNights();
		}
	}

	private validateSelectedArrivalDate(
		availability: AvailabilityDatesInterface,
	): void {
		if (!this.arrivalDate) {
			return;
		}

		const arrivalKey = dayjs(this.arrivalDate).format('YYYY-MM-DD');
		const departureKey = this.departureDate
			? dayjs(this.departureDate).format('YYYY-MM-DD')
			: null;

		// Skip validation ONLY for stepper updates
		// For all other cases (including manual/month navigation), validate dates
		// that are in the request range to ensure they're still available
		if (this._dateUpdateSource === 'stepper') {
			return;
		}

		// Validate both arrival and departure dates using shared helper
		const { isArrivalValid, isDepartureValid } = validateDates(
			availability,
			arrivalKey,
			departureKey || undefined,
		);

		if (!isArrivalValid || !isDepartureValid) {
			this.arrivalDate = null;
			this.departureDate = null;
			this._resetDateRange++;
			this.updateCta.update((v) => v + 1);
		}
	}

	private getRequestKey(request: AvailabilityRequestInterface): string {
		// Create a unique key based on key parameters (hotel, occupancy, etc.)
		// Date range is not included as we want to merge across date ranges
		return [
			request.hotelGUID,
			JSON.stringify(request.occupancies),
			JSON.stringify(request.accommodationTypes || []),
			request.dealGUID,
			request.packageGUID,
			request.roomGUID,
			request.promotionGUID,
			request.groupGUID,
			request.suppressAvailability,
		].join('|');
	}

	private mergeAvailability(
		newAvailability: AvailabilityDatesInterface,
		requestStartDate: string,
		requestEndDate: string,
		shouldReset: boolean,
	): AvailabilityDatesInterface {
		if (shouldReset) {
			this.mergedAvailability.set({ ...newAvailability });
			return this.mergedAvailability();
		}

		const merged = { ...this.mergedAvailability() };
		this.removeOldAvailabilityData(
			merged,
			requestStartDate,
			requestEndDate,
		);
		this.addNewAvailabilityData(
			merged,
			newAvailability,
			requestStartDate,
			requestEndDate,
		);

		this.mergedAvailability.set(merged);
		return merged;
	}

	private isDateInRange(
		date: string,
		startDate: string,
		endDate: string,
	): boolean {
		return date >= startDate && date <= endDate;
	}

	private removeOldAvailabilityData(
		merged: AvailabilityDatesInterface,
		requestStartDate: string,
		requestEndDate: string,
	): void {
		for (const availableArrivalDate in merged) {
			if (
				this.isDateInRange(
					availableArrivalDate,
					requestStartDate,
					requestEndDate,
				)
			) {
				delete merged[availableArrivalDate];
			} else {
				this.removeDepartureDatesInRange(
					merged,
					availableArrivalDate,
					requestStartDate,
					requestEndDate,
				);
			}
		}
	}

	private removeDepartureDatesInRange(
		merged: AvailabilityDatesInterface,
		arrivalDate: string,
		requestStartDate: string,
		requestEndDate: string,
	): void {
		const mergedDepartures = merged[arrivalDate];
		// Don't remove departure dates from arrival dates outside the request range
		// This preserves departure dates (e.g., Feb dates for Jan arrivals) when navigating to new months
		// Only remove if the arrival date itself is being replaced by new data
		if (this.isDateInRange(arrivalDate, requestStartDate, requestEndDate)) {
			// If arrival date is in range, it will be replaced entirely, so remove departures
			for (const availableDepartureDate in mergedDepartures) {
				if (
					this.isDateInRange(
						availableDepartureDate,
						requestStartDate,
						requestEndDate,
					)
				) {
					delete mergedDepartures[availableDepartureDate];
				}
			}
		}
		// If arrival date is outside range, keep all its departure dates
		if (Object.keys(mergedDepartures).length === 0) {
			delete merged[arrivalDate];
		}
	}

	private addNewAvailabilityData(
		merged: AvailabilityDatesInterface,
		newAvailability: AvailabilityDatesInterface,
		requestStartDate: string,
		requestEndDate: string,
	): void {
		for (const arrivalDate in newAvailability) {
			if (
				newAvailability[arrivalDate] &&
				this.isDateInRange(
					arrivalDate,
					requestStartDate,
					requestEndDate,
				)
			) {
				merged[arrivalDate] = {
					...newAvailability[arrivalDate],
				};
			}
		}
	}

	private hasDates(): boolean {
		return !!(this.initialArrivalDate && this.initialDepartureDate);
	}

	private calculateNights(): number {
		return dayjs(this.initialDepartureDate).diff(
			dayjs(this.initialArrivalDate),
			'days',
		);
	}

	private clearDatesAndResetNights(
		minNights = this.stepperConfig?.min ?? 1,
	): void {
		this.initialArrivalDate = null;
		this.initialDepartureDate = null;
		this.arrivalDate = null;
		this.departureDate = null;
		this._resetDateRange++;
		this.nights = minNights;
		this.updateCta.update((v) => v + 1);
	}

	onCtaEmit(event: Event, nextInput: AvailabilityWidgetInput = 'dates') {
		this.openInput.set(nextInput);

		event.stopPropagation();
	}

	onBackButton(
		event: Event,
		isHotelsOpen: boolean,
		isOccupancyOpen: boolean,
		isPackagesOpen: boolean,
		isDatePickerOpen: boolean,
	) {
		let inputToOpen: AvailabilityWidgetInput | null = null;

		if (isDatePickerOpen) inputToOpen = 'dates';
		else if (isPackagesOpen) inputToOpen = 'packages';
		else if (isOccupancyOpen) inputToOpen = 'occupancy';
		else if (isHotelsOpen) inputToOpen = 'hotels';

		this.openInput.set(inputToOpen);

		event.stopPropagation();
	}

	onDatePickerOpened(isOpen: boolean) {
		this.onInputOpenChange('dates', isOpen);
	}

	onOccupancyOpenEmit(isOpen: boolean) {
		this.onInputOpenChange('occupancy', isOpen);
	}

	onOccupancyEmit(occupancy: OccupancyInterface[]) {
		this.occupancy = occupancy;
		this.updateCta.update((v) => v + 1);
		this.occupancyChanged.emit(occupancy);
		this.getAvailability();
	}

	onHotelSelect(hotel: Partial<HotelInterface>) {
		this.selectedHotel.set(hotel);
		this.updateCta.update((v) => v + 1);
		this.hotelChanged.emit(hotel);
		this.getAvailability();
	}

	onPackageSelect(packageItem: AvailabilityInputDropdownItem) {
		// TODO: Fix typing in VPL-31625
		const currentPackageIndex = this.packages().findIndex(
			(item) =>
				(item.guid ?? item['GUID']) ===
				(packageItem.guid ?? packageItem.GUID),
		);

		this.selectedPackage.set(packageItem);
		this.packageSelected.emit(currentPackageIndex);
		this.resetDateRange();
		this.getAvailability();
	}

	resetDateRange() {
		this._resetDateRange++;
	}

	onInputOpenChange(input: AvailabilityWidgetInput, isOpen: boolean): void {
		const currentState = this.openInput();
		const newState = isOpen ? input : null;

		// Only update state if it's actually changing to prevent closing popover
		// when availability changes trigger change detection during month navigation
		if (currentState === newState) {
			return;
		}

		// Only close other inputs if we're opening a different input
		// This prevents flickering when the same input is already open
		if (isOpen && currentState !== input) {
			this.closeAllInputs();
		}

		this.setInputState(input, isOpen);
	}

	private closeAllInputs(): void {
		this.openInput.set(null);
	}

	private setInputState(
		input: AvailabilityWidgetInput,
		isOpen: boolean,
	): void {
		this.openInput.set(isOpen ? input : null);
	}

	recalculateAvailabilityForToday() {
		this.firstDateInCalendar = dayjs()
			.startOf('month')
			.format('YYYY-MM-DD');
		this.getAvailability();
	}

	onMonthChange(newFirstDate: string) {
		this.firstDateInCalendar = newFirstDate;
		this.jumpToFirstAvailableDate = false;
		this.cancelPendingDatePick();
		this._dateUpdateSource = 'manual';
		this.getAvailability();
	}

	onStepperUpdated(nights: number) {
		this._dateUpdateSource = 'stepper';
		this.nights = nights;
		this.cancelPendingDatePick();
		this.jumpToFirstAvailableDate = false;
		this.getAvailability();
	}

	private _availabilityFlushPending = false;

	getAvailability() {
		// Coalesce same-tick calls into a single microtask flush: ngOnInit,
		// ngOnChanges and several effects call this synchronously during mount,
		// and `distinctUntilChanged` alone can't dedup them once a transient
		// field (e.g. firstDateInCalendar) differs between calls.
		if (this._availabilityFlushPending) {
			return;
		}
		this._availabilityFlushPending = true;
		queueMicrotask(() => {
			this._availabilityFlushPending = false;
			this._emitAvailabilityRequest();
		});
	}

	private _emitAvailabilityRequest() {
		if (!isPlatformBrowser(this.platformId)) {
			return;
		}
		const accommodationTypes =
			this.activeAccommodationTypeIndex() === 0
				? []
				: [
						this.accommodationTypes()[
							this.activeAccommodationTypeIndex() - 1
						]?.guid,
					];

		// Calculate date range for visible months (2 months from firstDateInCalendar)
		const startDate = this.firstDateInCalendar;
		const endDate = dayjs(this.firstDateInCalendar)
			.add(2, 'month')
			.format('YYYY-MM-DD');

		const request: AvailabilityRequestInterface = {
			accommodationTypes,
			hotelGUID: this.hotelGuid
				? this.hotelGuid
				: this.selectedHotel()?.guid,
			startDate,
			endDate,
			occupancies: this.occupancy,
			dealGUID: this.dealGuid,
			packageGUID: this.showPackages()
				? (this.selectedPackage()?.['GUID'] ?? this.packageGuid())
				: undefined,
			roomGUID: this.roomGuid,
			promotionGUID: this.promotionGuid(),
			groupGUID: this.groupGuid(),
			suppressAvailability: this.suppressAvailability(),
		};

		if (this.stepperConfig?.useStepper) {
			request.nights = this.nights;
		}
		this.updateAvailability$.next(request);
	}

	updateModel(event) {
		this.arrivalDate = event.arrivalDate;
		this.departureDate = event.departureDate;
		this.updateCta.update((v) => v + 1);
		// Don't mark as manual here - we want to validate selected dates
		// The validation will only clear if dates are truly unavailable
		this.getAvailability();
	}

	bookingtoolQuery = computed(() => {
		this.updateCta();

		const accommodationType =
			this.activeAccommodationTypeIndex() === 0
				? undefined
				: this.accommodationTypes()[
						this.activeAccommodationTypeIndex() - 1
					]?.accommodationType;

		const effectiveArrivalDate =
			this.arrivalDate || this.initialArrivalDate;
		const effectiveDepartureDate =
			this.departureDate || this.initialDepartureDate;

		// `promotionCode` is intentionally omitted: the widget doesn't own
		// promotion state, so emitting it as `undefined` under
		// `queryParamsHandling: 'merge'` would clear the existing URL value.
		return {
			...BookingRedirectHelper.toQueryParamRecord({
				roomGuid: this.roomGuid,
				dealGuid: this.dealGuid,
				packageGuid:
					(this.selectedPackage()?.['GUID'] ||
						this.selectedPackage()?.guid) ??
					this.packageGuid(),
				accommodationTypes: accommodationType,
				occupancy: JSON.stringify(this.occupancy),
			}),
			startDate: effectiveArrivalDate
				? dayjs(effectiveArrivalDate).format('YYYY-MM-DD')
				: null,
			endDate: effectiveDepartureDate
				? dayjs(effectiveDepartureDate).format('YYYY-MM-DD')
				: null,
		};
	});

	isExternalUrl = computed<boolean>(() => !!this.bookingtoolUrl());

	currentHotelSlug = computed<string | undefined>(
		() => this.selectedHotel()?.slug ?? this.hotelSlug(),
	);

	bookingtoolHref = computed(() => {
		this.updateCta();
		const bookingtoolUrl = this.bookingtoolUrl();
		const hotelSlug = this.currentHotelSlug();

		if (
			!this.arrivalDate ||
			!this.departureDate ||
			!bookingtoolUrl ||
			!hotelSlug
		) {
			return null;
		}

		const accommodationType =
			this.activeAccommodationTypeIndex() === 0
				? undefined
				: this.accommodationTypes()[
						this.activeAccommodationTypeIndex() - 1
					]?.accommodationType;

		const bookingUrl = BookingRedirectHelper.getBookingToolUrl(
			bookingtoolUrl,
			hotelSlug,
			{
				roomGuid: this.roomGuid,
				dealGuid: this.dealGuid,
				packageGuid:
					(this.selectedPackage()?.['GUID'] ||
						this.selectedPackage()?.guid) ??
					this.packageGuid(),
				languageCode: this.language(),
				accommodationTypes: accommodationType,
				startDate: dayjs(this.arrivalDate).format('YYYY-MM-DD'),
				endDate: dayjs(this.departureDate).format('YYYY-MM-DD'),
				occupancy: JSON.stringify(this.occupancy),
			},
		);

		return bookingUrl;
	});

	isCtaDisabled = computed<boolean>(() => {
		this.updateCta();

		// Missing dates do NOT disable the CTA: `onCtaClick` intercepts that case
		// to open the date picker. Disabling is only about an unmade required
		// selection (hotel/package).
		return (
			(this.showHotels() && !this.selectedHotel()) ||
			(this.showPackages() && !this.selectedPackage()?.name)
		);
	});

	hasIncompleteDates = computed<boolean>(() => {
		this.updateCta();

		return this.showDates() && (!this.arrivalDate || !this.departureDate);
	});

	routerLink = computed<string[] | null>(() =>
		this.hasIncompleteDates() || this.isCtaDisabled() ? null : ['./'],
	);

	localizedBookingHref = computed<string>(() => {
		const isDefaultLanguage = this.language() === this.defaultLanguage;
		const path = isDefaultLanguage
			? '/booking'
			: `/${this.language()}/booking`;

		const search = toQueryString(this.bookingtoolQuery());

		return search ? `${path}?${search}` : path;
	});

	vhiBookingHref = computed<string | null>(() => {
		this.updateCta();
		const code = this.selectedVhiCode();

		if (!this.arrivalDate || !this.departureDate || !code) {
			return null;
		}

		return generateVhiBookingUrl({
			code,
			language: this.language(),
			startDate: dayjs(this.arrivalDate).format('YYYY-MM-DD'),
			endDate: dayjs(this.departureDate).format('YYYY-MM-DD'),
			occupancy: this.occupancy ?? [],
		});
	});

	ctaHref = computed<string | null>(() => {
		if (this.isVhiHotelSelected()) {
			return this.vhiBookingHref();
		}

		return this.isExternalUrl()
			? this.bookingtoolHref()
			: this.localizedBookingHref();
	});

	onCtaClick(event: Event) {
		// EventPhase.REPLAY = 0: skip during Angular SSR event replay
		if (event.eventPhase !== 0) {
			if (!this.arrivalDate || !this.departureDate) {
				event.preventDefault();

				if (this.showHotels() && !this.selectedHotel()) return;

				this.onDatePickerOpened(true);
				return;
			}

			if (this.isCtaDisabled()) {
				event.preventDefault();
				return;
			}

			this.closeAllInputs();
			this.setEmittedData();
		}
	}

	setEmittedData() {
		const eventType = () => {
			if (this.packageGuid()) {
				return 'packages';
			} else if (this.roomGuid) {
				return 'rooms';
			} else if (this.dealGuid) {
				return 'deals';
			} else {
				return 'default';
			}
		};
		const rooms = this.occupancy.map((occupancy, i) => ({
			item_name: `ROOM${i + 1}`,
			numAdults: occupancy.adults,
			numChildren: occupancy.children,
			numBabies: occupancy.infants,
		}));
		const dayTypeBooking =
			dayjs().day() === 0 || dayjs().day() === 6 ? 'weekend' : 'weekday';
		const dayTypeVisit =
			dayjs(this.arrivalDate).day() === 5 ||
			dayjs(this.arrivalDate).day() === 6
				? 'weekend'
				: 'weekday';

		this.actionClick.emit({
			event: 'search_availability',
			eventType: eventType(),
			data: {
				dayTypeBooking,
				dayTypeVisit,
				dealGuid: this.dealGuid,
				hotelGuid: this.hotelGuid,
				leadtime: dayjs(this.arrivalDate).diff(
					dayjs().format('YYYY-MM-DD'),
					'day',
				),
				numGuests: this.occupancy.reduce((acc, curr) => {
					return acc + curr.adults + curr.children + curr.infants;
				}, 0),
				numRooms: this.occupancy.length,
				numNights: dayjs(this.departureDate).diff(
					dayjs(this.arrivalDate),
					'days',
				),
				packageGuid:
					(this.selectedPackage()?.['GUID'] ||
						this.selectedPackage()?.guid) ??
					this.packageGuid(),
				roomGuid: this.roomGuid,
				rooms,
				selectedArrivalDate: dayjs(this.arrivalDate).format(
					'YYYY-MM-DD',
				),
				selectedDepartureDate: dayjs(this.departureDate).format(
					'YYYY-MM-DD',
				),
			},
		});
	}
}

/**
 * Compares two AvailabilityRequestInterface objects for equality,
 * used primarily for the distinctUntilChanged operator.
 * It performs a shallow comparison of most properties and a deep comparison
 * for `occupancies` and `accommodationTypes` arrays using JSON.stringify.
 * @param prev The previous AvailabilityRequestInterface object.
 * @param curr The current AvailabilityRequestInterface object.
 * @returns True if the requests are considered equal, false otherwise.
 */
export function areAvailabilityRequestsEqual(
	prev: AvailabilityRequestInterface,
	curr: AvailabilityRequestInterface,
): boolean {
	if (!prev || !curr) return false;
	const shallowEqual =
		prev.hotelGUID === curr.hotelGUID &&
		prev.startDate === curr.startDate &&
		prev.endDate === curr.endDate &&
		prev.dealGUID === curr.dealGUID &&
		prev.packageGUID === curr.packageGUID &&
		prev.roomGUID === curr.roomGUID &&
		prev.nights === curr.nights &&
		prev.promotionGUID === curr.promotionGUID &&
		prev.groupGUID === curr.groupGUID &&
		prev.suppressAvailability === curr.suppressAvailability;
	const occupanciesEqual =
		JSON.stringify(prev.occupancies) === JSON.stringify(curr.occupancies);
	const accommodationTypesEqual =
		JSON.stringify(prev.accommodationTypes) ===
		JSON.stringify(curr.accommodationTypes);
	return shallowEqual && occupanciesEqual && accommodationTypesEqual;
}
